/** Legacy iframe-coordinate diagnostic utility. InteractiveReview now draws
 * annotations inside the native page: full iframe size cannot locate a page
 * inside Steel's browser chrome and letterboxing. Keep this pure helper for
 * existing callers and the recorded-geometry regression; never use it to
 * position a streamed-review marker without the actual page canvas bounds. */

export interface BridgeRect {
  /** Left edge in remote-viewport CSS pixels. From getBoundingClientRect().x */
  x: number;
  /** Top edge in remote-viewport CSS pixels. From getBoundingClientRect().y */
  y: number;
  /** Element width in remote-viewport CSS pixels. */
  width: number;
  /** Element height in remote-viewport CSS pixels. */
  height: number;
}

export interface RenderedSize {
  /** Pixel width of the rendered iframe element (measured from the DOM or a ref). */
  width: number;
  /** Pixel height of the rendered iframe element (measured from the DOM or a ref). */
  height: number;
}

export interface RemoteViewport {
  /** Width the remote browser session was launched with (Steel viewportWidth). */
  width: number;
  /** Height the remote browser session was launched with (Steel viewportHeight). */
  height: number;
}

export interface ScaledRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Fit the complete viewer without changing the remote capture viewport.
 * CSS transforms scale the entire provider iframe, including any intrinsic
 * minimum canvas width, rather than giving its app a narrow mobile viewport. */
export function fitRemoteViewport(container: RenderedSize, viewport: RemoteViewport): { scale: number; left: number; top: number } {
  if (![container.width, container.height, viewport.width, viewport.height].every(value => Number.isFinite(value) && value > 0)) return { scale: 0, left: 0, top: 0 };
  const scale = Math.min(container.width / viewport.width, container.height / viewport.height);
  return { scale, left: (container.width - viewport.width * scale) / 2, top: (container.height - viewport.height * scale) / 2 };
}

/**
 * Scale a remote-space rect into rendered-iframe-space pixels.
 *
 * Formula:
 *   scaleX = renderedSize.width  / remoteViewport.width
 *   scaleY = renderedSize.height / remoteViewport.height
 *   left   = rect.x * scaleX
 *   top    = rect.y * scaleY
 *   width  = rect.width  * scaleX
 *   height = rect.height * scaleY
 *
 * LIVE-VERIFY: if the Steel viewer letterboxes or pads the canvas (e.g. a
 * browser chrome header inside the iframe), add the measured pixel offset to
 * `left` and `top` here after confirming over the real stream.
 */
export function scaleRect(
  rect: BridgeRect,
  renderedSize: RenderedSize,
  remoteViewport: RemoteViewport,
): ScaledRect {
  const scaleX = renderedSize.width / remoteViewport.width;
  const scaleY = renderedSize.height / remoteViewport.height;
  return {
    left: rect.x * scaleX,
    top: rect.y * scaleY,
    width: rect.width * scaleX,
    height: rect.height * scaleY,
  };
}
